import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { supabase } from './supabaseClient';

const AuthContext = createContext(null);

const MEMBERSHIP_LEVELS = {
  free_observer: 0,
  doer: 1,
  spark_seeker: 1,
  momentum_builder: 2,
  growth_mastermind: 3,
  tu_radar: 1,
  clarity_mentor: 4,
  onsite_mexico: 5,
  admin: 99,
};

const MEMBERSHIP_ESCRITORIO = {
  free_observer: null,
  doer: '/arbol',
  spark_seeker: '/arbol',
  momentum_builder: '/escritorio/momentum',
  growth_mastermind: '/escritorio/momentum',
  tu_radar: '/escritorio/tu-radar',
  clarity_mentor: null,
  onsite_mexico: '/escritorio/onsite',
  admin: '/escritorio/momentum',
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [viewAs, setViewAsState] = useState(() => {
    try { return localStorage.getItem('td_admin_viewAs') || null; } catch { return null; }
  });
  const setViewAs = (val) => {
    setViewAsState(val);
    try { if (val) localStorage.setItem('td_admin_viewAs', val); else localStorage.removeItem('td_admin_viewAs'); } catch {}
  };
  const resolved = useRef(false);

  const fetchProfile = async (userId) => {
    console.log('[Auth] fetchProfile called with userId:', userId);
    try {
      const { data, error, status } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      console.log('[Auth] fetchProfile response — status:', status, 'error:', error, 'data:', data);

      if (error) {
        console.error('[Auth] fetchProfile FAILED:', error.message, '| code:', error.code, '| hint:', error.hint);
        // If profile doesn't exist (406/PGRST116), try to create it
        if (error.code === 'PGRST116') {
          console.log('[Auth] Profile not found, attempting to create...');
          const { data: { user: authUser } } = await supabase.auth.getUser();
          if (authUser) {
            const { error: insertError } = await supabase.from('profiles').upsert({
              id: authUser.id,
              email: authUser.email,
              full_name: authUser.user_metadata?.full_name || null,
              avatar_url: authUser.user_metadata?.avatar_url || null,
            }, { onConflict: 'id' });
            if (insertError) {
              console.error('[Auth] Profile create FAILED:', insertError.message);
            } else {
              console.log('[Auth] Profile created, re-fetching...');
              const { data: data2 } = await supabase.from('profiles').select('*').eq('id', userId).single();
              console.log('[Auth] Re-fetched profile:', data2);
              return data2;
            }
          }
        }
        return null;
      }
      return data;
    } catch (e) {
      console.error('[Auth] fetchProfile EXCEPTION:', e);
      return null;
    }
  };

  const handleSession = async (session, source) => {
    if (resolved.current) return;
    resolved.current = true;
    console.log('[Auth] handleSession from:', source);

    if (session?.user) {
      console.log('[Auth] User:', session.user.email);
      setUser(session.user);
      setLoading(false);
      const p = await fetchProfile(session.user.id);
      console.log('[Auth] Profile:', p?.membership, p?.onboarding_done);
      setProfile(p);
      setProfileLoaded(true);
    } else {
      console.log('[Auth] No session');
      setUser(null);
      setProfile(null);
      setProfileLoaded(true);
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('[Auth] AuthProvider mounted, setting up listeners...');

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('[Auth] onAuthStateChange:', event);
      handleSession(session, 'onAuthStateChange:' + event);
    });

    // Fallback after 3s
    const fallbackTimer = setTimeout(async () => {
      if (!resolved.current) {
        console.log('[Auth] Fallback triggered (3s timeout)');
        const { data: { session } } = await supabase.auth.getSession();
        handleSession(session, 'fallback:getSession');
      }
    }, 3000);

    // Hard timeout after 6s
    const hardTimer = setTimeout(() => {
      if (!resolved.current) {
        console.log('[Auth] HARD TIMEOUT (6s) — forcing loading=false');
        resolved.current = true;
        setProfileLoaded(true);
        setLoading(false);
      }
    }, 6000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(fallbackTimer);
      clearTimeout(hardTimer);
    };
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
    resolved.current = false;
  };

  const refreshProfile = async () => {
    if (user) {
      console.log('[Auth] refreshProfile called');
      const p = await fetchProfile(user.id);
      console.log('[Auth] refreshProfile result:', p?.membership);
      setProfile(p);
    }
  };

  const rawMembership = profile?.membership;
  const realMembership = (rawMembership && typeof rawMembership === 'string') ? rawMembership.trim().toLowerCase() : 'free_observer';
  const realIsAdmin = realMembership === 'admin' || profile?.email === 'thinkanddo.er@gmail.com';
  // Admin always gets admin membership unless viewing as another tier
  const membership = realIsAdmin ? (viewAs || 'admin') : realMembership;
  console.log('[Auth] Context render — raw:', JSON.stringify(rawMembership), '| resolved:', membership, '| viewAs:', viewAs, '| profile exists:', !!profile);
  const membershipLevel = MEMBERSHIP_LEVELS[membership] || 0;
  const isAdmin = realIsAdmin;
  const onboardingDone = profile?.onboarding_done || false;
  const escritorioPath = MEMBERSHIP_ESCRITORIO[membership] || null;

  const hasAccess = (requiredMembership) => {
    if (isAdmin) return true;
    const required = MEMBERSHIP_LEVELS[requiredMembership] || 0;
    return membershipLevel >= required;
  };

  const value = {
    user,
    profile,
    loading,
    profileLoaded,
    membership,
    membershipLevel,
    isAdmin,
    onboardingDone,
    escritorioPath,
    hasAccess,
    signOut,
    refreshProfile,
    viewAs,
    setViewAs,
    isPreview: isAdmin && !!viewAs,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
